import { merge } from 'lodash'

import { injectGlobal } from '../core/ds/index.js'
import { createTheme } from './index'

const defaultConf = {
  name: 'BusinessTheme',
  version: '2.0.0',
  useRem: false,
  space: {
    x: '8px',
    '2x': '16px',
    '3x': '24px',
    '4x': '32px',
    '5x': '40px',
    '6x': '48px',
    '7x': '56px',
    '8x': '64px',
    '9x': '72px',
    '10x': '80px',
    xs: '4px',
    '2xs': '8px',
    '3xs': '12px',
    '4xs': '16px',
    '5xs': '20px',
    '6xs': '24px',
    '7xs': '28px',
    '8xs': '32px',
    '9xs': '36px',
    '10xs': '40px',
  },
  fonts: {
    default: '"RedNum", "RedZh", "RedEn", -apple-system',
    pfLight: '"PingFangSC-Light", "Microsoft YaHei"',
    pfRegular: '"PingFangSC-Regular", "Microsoft YaHei"',
  },
  fontWeights: {
    normal: '400',
    medium: '700',
    thin: '300',
  },
  fontSizes: {
    12: '12px',
    14: '14px',
    16: '16px',
    20: '20px',
    24: '24px',
    26: '26px',
    28: '28px',
    32: '32px',
  },
  lineHeights: {
    0: 0,
    12: '12px',
    14: '14px',
    16: '16px',
    20: '20px',
    24: '24px',
    26: '26px',
    28: '28px',
    32: '32px',
  },
  palette: {
    blue: '#3a64ff',
    navy: '#e5ebff',
    white: '#ffffff',

    dark: '#2d2d2d',
    grey: '#888888',
    grey1: '#cccccc',

    green: '#28BC77',
    yellow: '#ffad33',
    red: '#ff525d',

    navy1: '#d1d5e6',
    navy2: '#eef1f8',
    navy3: '#f7f8fb',
    navy4: '#f9fafd',
    grey2: '#d9d9d9',
    grey3: '#e8e8e8',
    grey4: '#fafafa',

    tinBlue: '#f2f7ff',
    tinGreen: '#f0fcf6',
    tinYellow: '#fff8e5',
    tinRed: '#fdecec',
  },
  colors: {
    brand: 'blue',
    brandPa: 'navy',
    primary: 'blue',
    info: 'green',
    warn: 'yellow',
    danger: 'red',
    success: 'green',
    error: 'red',
    outline: 'grey2',
    board: 'navy2',
    card: 'white',
    body: 'dark',
    mute: 'grey',
    ash: 'grey1',
  },
  radii: {
    card: '8px',
    control: '4px',
    button: '4px',
    circle: '100%',
    pill: '9999px',
  },
  borderWidths: {
    outline: 1,
  },
  borderColors: [
    'brand',
    'primary',
    'info',
    'warn',
    'danger',
    'outline',
    'success',
  ],
  shadows: {
    level1: '0px 2px 6px 0px #e4e6f2',
    level2: '0px 2px 8px 0px rgba(0,15,78,0.1)',
    level3: '0px 2px 12px 0px rgba(0,15,78,0.1)',
    level4: '0px 6px 16px 0px rgba(0,15,78,0.1)',
  },

  // configuration
  breakpoints: ['640px', '1024px', '1400x'],
  global: {
    ':focus': {
      outline: '2px auto #ced8ff',
      outlineOffset: '2px',
    },
    div: {
      boxSizing: 'border-box',
    },
    body: {
      WebkitFontSmoothing: 'antialiased',
      fontFamily: '"RedNum", "RedZh", "RedEn", -apple-system',
      background: '#eef1f8',
      color: '#2d2d2d',
      margin: 0,
    },
    fontFaces: [
      {
        fontFamily: 'RedZh',
        rangeType: 'zh',
        weight: 400,
        source: [
          'PingFangSC-Regular',
          'Microsoft YaHei',
        ],
      },
      {
        fontFamily: 'RedZh',
        rangeType: 'zh',
        weight: 700, // bold
        source: [
          'PingFangSC-Medium',
          'Microsoft YaHei',
        ],
      },
      {
        fontFamily: 'RedZh',
        rangeType: 'zh',
        weight: 300, // light
        source: [
          'PingFangSC-Light',
          'Microsoft YaHei', // 使用雅黑字体
        ],
      },
      {
        fontFamily: 'RedNum',
        // 30-39
        rangeType: 'num',
        source: [
          'Avennir',
          'Helvetica Neue',
        ],
      },
      {
        fontFamily: 'RedEn',
        source: [
          'Arial',
          'Helvetica Neue',
        ],
      },
    ],
  },
}

export default function createBizTheme(config = {}) {
  const confs = merge({}, defaultConf, config)

  const { global = {}, ...restConf } = confs
  const { fontFaces = [], ...restGlobal } = global

  const businessTheme = createTheme(restConf)

  const rangeDict = {
    // ff00-ffef 全角ASCII、全角中英文标点 http://www.unicode.org/charts/PDF/UFF00.pdf
    // 3000-303F CJK标点符号 http://www.unicode.org/charts/PDF/U3000.pdf
    // 4E00-9FA5 基本汉字 20902字
    zh: 'U+ff00-ffef, U+3000-303f, U+4e00-9fa5',
    num: 'U+30-39',
  }

  injectGlobal(restGlobal)
  injectGlobal(fontFaces.map(f => {
    const src = f.source.map(s => `local('${s}')`).join(', ')
    const weight = f.weight || '"normal"'
    let unicodeRange = ''
    if (f.rangeType) {
      unicodeRange = `unicode-range: ${rangeDict[f.rangeType]}`
    }
    return `
      @font-face {
        font-family: '${f.fontFamily}';
        src: ${src};
        font-weight: ${weight};
        ${unicodeRange}
      }
    `
  }).join('\n'))
  return businessTheme
}
